import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTracking } from '../../hooks/usePageTracking';
import { useTelemetry } from '../../hooks/useTelemetry';
import { MODULES } from '../../telemetry/constants/modules';
import { useGmbConnection } from './hooks/useGmbConnection';
import type { ConnectionData } from './hooks/useGmbConnection';
import GMBHeader from './components/GMBHeader';
import GMBSidebar from './components/GMBSidebar';
import GMBTooltip from './components/GMBTooltip';
import GMBConnectionGate from './components/GMBConnectionGate';
import GMBNoProfileFallback from './components/GMBNoProfileFallback';
import GMBLockedDashboard from './components/GMBLockedDashboard';
import PlaceholderPage from './components/PlaceholderPage';
import StrategyOverview from './components/StrategyOverview';
import AsIsState from './components/AsIsState';
import GoalSetting from './components/GoalSetting';
import ActionPlan from './components/ActionPlan';
import ImplementationOverview from './components/implementation/ImplementationOverview';
import PhotoUpload from './components/implementation/PhotoUpload';
import PostScheduler from './components/implementation/PostScheduler';
import ReviewManagement from './components/implementation/ReviewManagement';
import QAAndChecklist from './components/implementation/QAAndChecklist';
import GovernanceOverview from './components/governance/GovernanceOverview';
import LivePreview from './components/governance/LivePreview';
import ActivityLog from './components/governance/ActivityLog';
import AlertsAndPermissions from './components/governance/AlertsAndPermissions';
import PublishingActivityMonitoring from './components/governance/PublishingActivityMonitoring';
import SearchPerformanceTrends from './components/optimization/SearchPerformanceTrends';
import ReviewReputationInsights from './components/optimization/ReviewReputationInsights';
import ContentEngagementPerformance from './components/optimization/ContentEngagementPerformance';
import GMBLocationPicker from './components/GMBLocationPicker';
import { GMB_BOOKING_URL } from './constants';
import MonthlyOptimizationAudit from './components/optimization/MonthlyOptimizationActionPlan';

export default function GMBManager() {
  usePageTracking({ moduleName: MODULES.GMB_MANAGER, submoduleName: 'GMB Manager', routePath: '/gmb-manager' });
  const { trackAction } = useTelemetry({ module: MODULES.GMB_MANAGER });
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('strategy-overview');
  const [darkMode, setDarkMode] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState({ title: '', content: '' });
  const handleGmbConnected = useCallback((_freshData?: ConnectionData | null) => {
    setActiveSection('strategy-overview');
  }, []);

  const {
    isChecking,
    isConnected,
    isOAuthLoading,
    isSyncing,
    connectionData,
    profileSetup,
    error,
    showGate,
    showLocked,
    showNoProfile,
    showLocationPicker,
    showSetupScreen,
    checkConnection,
    handleOAuthLogin,
    handleSkip,
    handleConnectFromLocked,
    handleSync,
    handleDisconnect,
    setError,
  } = useGmbConnection(handleGmbConnected);

  // All GMB onboarding states share the standalone onboarding route so
  // connected and unconnected accounts enter the same flow.
  // Wait for the connection check: isConnected starts false, so redirecting
  // before it resolves sends connected users to onboarding, which sends them
  // straight back here.
  useEffect(() => {
    if (isChecking) return;
    if (showGate || showSetupScreen) {
      navigate('/gmb-onboarding');
    }
  }, [isChecking, showGate, showSetupScreen, navigate]);

  const handleTooltipShow = (title: string, content: string) => {
    setTooltipContent({ title, content });
    setShowTooltip(true);
  };

  const pageInfo = useMemo(() => {
    const infoMap: Record<string, { title: string; subtitle: string }> = {
      'strategy-overview': { title: 'Strategy Overview', subtitle: 'Your complete GMB strategy at a glance' },
      'as-is-state': { title: 'As-Is State', subtitle: 'Your current GMB baseline' },
      'goal-setting': { title: 'Goal Setting', subtitle: 'Define targets and KPIs' },
      'action-plan': { title: 'Action Plan', subtitle: 'Technical setup and content execution' },
      'implementation-overview': { title: 'Implementation Overview', subtitle: 'Build and execute your GMB profile' },
      'photo-upload': { title: 'Photo Upload', subtitle: 'Upload and manage your GMB photos' },
      'post-scheduler': { title: 'Post Scheduler', subtitle: 'Plan and schedule your GMB posts' },
      'review-management': { title: 'Review Management', subtitle: 'Manage and respond to customer reviews' },
      'qa-checklist': { title: 'Q&A Management', subtitle: 'Manage questions and track completion' },
      'governance-overview': { title: 'Governance Overview', subtitle: 'Monitor, audit and control your listing' },
      'live-preview': { title: 'Live Preview', subtitle: 'See how your listing appears on Google' },
      'activity-log': { title: 'Activity Log', subtitle: 'Full audit trail of all changes' },
      'alerts-permissions': { title: 'Alerts & Notifications', subtitle: 'Monitor your Google Business Profile for critical changes and alerts' },
      'search-performance-trends': { title: 'Search Performance Trends', subtitle: 'Performance trends and search query insights' },
      'review-reputation-insights': { title: 'Review & Reputation Insights', subtitle: 'Analyze review trends and reputation patterns' },
      'content-engagement-performance': { title: 'Content & Engagement Performance', subtitle: 'Content effectiveness and customer interaction patterns' },
      'monthly-optimization-action-plan': { title: 'Monthly Optimization & Action Plan', subtitle: 'Action recommendations and audit improvement tracking' },
      'publishing-monitoring': { title: 'Publishing & Activity Monitoring', subtitle: 'Track content publishing consistency and overall GMB activity levels' },
    };
    return infoMap[activeSection] || { title: 'GMB Manager', subtitle: 'Manage your Google Business Profile' };
  }, [activeSection]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    trackAction('GMB_NAVIGATE', `Navigated to ${section}`);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'strategy-overview':
        return <StrategyOverview onNavigate={handleSectionChange} />;
      case 'as-is-state':
        return <AsIsState onTooltipShow={handleTooltipShow} />;
      case 'goal-setting':
        return <GoalSetting onTooltipShow={handleTooltipShow} />;
      case 'action-plan':
        return <ActionPlan onTooltipShow={handleTooltipShow} />;
      case 'implementation-overview':
        return <ImplementationOverview onNavigate={handleSectionChange} onTooltipShow={handleTooltipShow} />;
      case 'photo-upload':
        return <PhotoUpload onTooltipShow={handleTooltipShow} />;
      case 'post-scheduler':
        return <PostScheduler onTooltipShow={handleTooltipShow} />;
      case 'review-management':
        return <ReviewManagement onTooltipShow={handleTooltipShow} />;
      case 'qa-checklist':
        return <QAAndChecklist onTooltipShow={handleTooltipShow} />;
      case 'governance-overview':
        return <GovernanceOverview onNavigate={handleSectionChange} />;
      case 'live-preview':
        return <LivePreview onNavigate={handleSectionChange} />;
      case 'activity-log':
        return <ActivityLog />;
      case 'alerts-permissions':
        return <AlertsAndPermissions onNavigate={handleSectionChange} />;
      case 'publishing-monitoring':
        return <PublishingActivityMonitoring onNavigate={handleSectionChange} />;
      case 'search-performance-trends':
        return (
          <SearchPerformanceTrends
            darkMode={darkMode}
            onNavigate={handleSectionChange}
          />
        );
      case 'review-reputation-insights':
        return (
          <ReviewReputationInsights
            darkMode={darkMode}
            onNavigate={handleSectionChange}
          />
        );
      case 'content-engagement-performance':
        return (
          <ContentEngagementPerformance
            darkMode={darkMode}
            onNavigate={handleSectionChange}
          />
        );
      case 'monthly-optimization-action-plan':
        return (
          <MonthlyOptimizationAudit
            darkMode={darkMode}
            onNavigate={handleSectionChange}
          />
        );
      default:
        return <StrategyOverview onNavigate={handleSectionChange} />;
    }
  };

  const renderMainContent = () => {
    if (isChecking) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Checking GMB connection...
            </p>
          </div>
        </div>
      );
    }

    if (showGate) {
      return (
        <GMBConnectionGate
          onConnect={handleOAuthLogin}
          onSkip={handleSkip}
          isLoading={isOAuthLoading}
          error={error}
          darkMode={darkMode}
          profileSetup={profileSetup}
        />
      );
    }

    if (showLocked) {
      return (
        <GMBLockedDashboard
          onConnect={handleConnectFromLocked}
          darkMode={darkMode}
        />
      );
    }

    if (showNoProfile) {
      return (
        <GMBNoProfileFallback
          onSkip={handleSkip}
          darkMode={darkMode}
        />
      );
    }

    if (showLocationPicker) {
      return <GMBLocationPicker darkMode={darkMode} onSelected={() => void checkConnection()} />;
    }

    // The shared onboarding route handles both profile connection and
    // mandatory setup before the dashboard is rendered.
    if (showGate || showSetupScreen) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
        </div>
      );
    }

    // BRD edge case 2: profile exists but is not verified (no green badge).
    if (connectionData?.verification_status === 'unverified') {
      return (
        <>
          <div className="mb-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-2">
              <i className="ri-shield-check-line mt-0.5 text-lg"></i>
              <p>
                <span className="font-semibold">Your Google Business Profile isn't verified.</span>{' '}
                Complete verification to unlock full functionality. Until it's verified, Google won't show
                your listing fully in search results or Maps.
              </p>
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <a
                href="https://business.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700"
              >
                Verify on Google
              </a>
              <a
                href={GMB_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-amber-300 px-3 py-1.5 font-medium hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/40"
              >
                Book a free 15-minute call
              </a>
            </div>
          </div>
          {renderContent()}
        </>
      );
    }

    return renderContent();
  };

  return (
    <div className={`h-screen overflow-hidden ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} transition-colors duration-300`}>
      <div className="flex h-full">
        <GMBSidebar
          activeSection={activeSection}
          setActiveSection={handleSectionChange}
          darkMode={darkMode}
        />

        {/* min-w-0 lets wide tables scroll inside their own box instead of widening the page */}
        <div className="flex-1 min-w-0 flex flex-col h-full">
          <GMBHeader
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            pageTitle={pageInfo.title}
            pageSubtitle={pageInfo.subtitle}
            isConnected={isConnected}
            connectionData={connectionData}
            onConnect={showLocked ? handleConnectFromLocked : handleOAuthLogin}
            onSync={handleSync}
            onDisconnect={handleDisconnect}
            isSyncing={isSyncing}
            onNavigate={setActiveSection}
          />

          <main className="flex-1 p-6 overflow-auto">
            {renderMainContent()}
          </main>
        </div>
      </div>

      {showTooltip && (
        <GMBTooltip
          title={tooltipContent.title}
          content={tooltipContent.content}
          onClose={() => setShowTooltip(false)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}
